import { useState, useEffect } from 'react';
import { supabase } from '../providers/supabase/supabase'; // Fixed import path

export const useArtistEmailData = (artistId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!artistId) return;

    const fetchCompleteArtistData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all data sources in parallel
        const [regressionResponse, artistsResponse, tracksResponse, outreachReportResponse] = await Promise.all([
          supabase
            .from('regression_prospects')
            .select('*')
            .eq('artist_id', artistId)
            .order('priority_score', { ascending: false })
            .limit(1),

          supabase
            .from('artists')
            .select('*')
            .eq('id', artistId)
            .single(),

          supabase
            .from('tracks')
            .select('*')
            .eq('artist_id', artistId)
            .limit(1)
            .single(),

          supabase
            .from('production_outreach_reports')
            .select('*')
            .eq('artist_id', artistId)
            .single()
        ]);

        // Handle any errors from the parallel requests (regression and outreach are optional)
        if (artistsResponse.error) throw artistsResponse.error;
        if (tracksResponse.error) throw tracksResponse.error;
        // Note: regressionResponse.error and outreachReportResponse.error are okay - not all artists have these

        // Get the first record from regression_prospects (optional)
        const regressionRecord = regressionResponse.data?.[0] || {};

        // Get GEMS data using track ISRC (if available)
        let gemsData = null;
        if (tracksResponse.data?.isrc) {
          const { data: gems, error: gemsError } = await supabase
            .from('gems_complete_analysis')
            .select('*')
            .eq('isrc', tracksResponse.data.isrc)
            .single();

          if (!gemsError && gems) {
            gemsData = gems;
          }
        }

        // Get latest artists_history data for fallback metrics
        let historyData = null;
        try {
          const { data: history, error: historyError } = await supabase
            .from('artists_history')
            .select('*')
            .eq('artist_id', artistId)
            .order('snapshot_date', { ascending: false })
            .limit(1);
          
          if (!historyError && history?.[0]) {
            historyData = history[0];
          }
        } catch (err) {
          console.log('No history data available for fallback');
        }

        // Combine all data into the structure our component expects
        const completeData = {
          ...regressionRecord,
          artist_id: artistId,
          // Flatten key fields for email templates
          name: artistsResponse.data?.name,
          email: regressionRecord.email || artistsResponse.data?.email || 'N/A',
          // Use history data as fallback for metrics
          spotify_monthly_listeners: regressionRecord.spotify_monthly_listeners || historyData?.spotify_monthly_listeners,
          spotify_followers: regressionRecord.spotify_followers || historyData?.spotify_followers,
          instagram_followers: regressionRecord.instagram_followers || historyData?.instagram_followers,
          tiktok_followers: regressionRecord.tiktok_followers || historyData?.tiktok_followers,
          // Keep nested structure for reference
          artists_data: artistsResponse.data,
          tracks_data: tracksResponse.data,
          gems_data: gemsData,
          history_data: historyData,
          outreach_report: outreachReportResponse.data || null
        };

        setData(completeData);
      } catch (err) {
        console.error('🚨 CRITICAL ERROR fetching artist email data:', err);
        console.error('🚨 Error details:', {
          message: err.message,
          code: err.code,
          details: err.details,
          hint: err.hint,
          artistId: artistId
        });
        setError(err.message);
        setData(null); // Explicitly set to null on error
      } finally {
        setLoading(false);
      }
    };

    fetchCompleteArtistData();
  }, [artistId]);

  return { data, loading, error };
};